<template>
  <div class="subscription-page">
    <div v-if="isLoading">
      <CanPayLoader />
    </div>
    <div class="container pt-3 pb-4">
      <div class="filters-card">
        <button class="filter-toggle" @click="filterExpanded = !filterExpanded">
          <span class="filter-toggle-title">Filter & Search</span>
          <span class="filter-toggle-icon">{{ filterExpanded ? '−' : '+' }}</span>
        </button>

        <div v-show="filterExpanded" class="filters-body">
          <input
            v-model.trim="filters.searchText"
            class="filter-input filter-search"
            type="text"
            placeholder="Search amount, title, or description"
            @keyup.enter="applyFilters"
          />

          <div class="filters-grid">
            <select v-model="filters.statusCode" class="filter-input">
              <option value="">All Status</option>
              <option value="204">Active</option>
              <option value="205">Failed</option>
              <option value="1203">Failed / Consumer Declined</option>
            </select>
            <SubscriptionDatePicker class="date-filter" v-model="filters.datePreset" @change="onDatePresetChange" />
          </div>

          <div class="filter-actions">
            <button class="apply-filter-btn" @click="applyFilters">Apply</button>
            <button class="clear-filter-btn" @click="resetFilters">Clear</button>
          </div>
        </div>
      </div>

      <div v-if="filteredSubscriptions.length === 0" class="text-center text-white mt-5">
        No subscriptions found.
      </div>
      <div v-for="item in filteredSubscriptions" :key="item.id" class="subscription-card" @click="openDetails(item)">
        <div class="card-top-row">
          <div class="subscription-title">{{ item.membership_program_title || 'Membership Program' }}</div>
          <div class="amount">${{ formatMoney(item.amount) }}</div>
        </div>
        <div class="subscription-reference" v-if="item.subscription_reference">{{ item.subscription_reference }}</div>
        <div class="subscription-description" v-if="item.membership_description">{{ item.membership_description }}</div>
        <div class="period-chip">{{ formatPeriod(item.membership_period) }}</div>
        <div class="meta-separator"></div>
        <div class="meta-row">
          <span class="meta-label">Status</span>
          <span class="meta-value" :class="isActiveStatus(item) ? 'status-pill' : 'status-pill-inactive'">{{ getSubscriptionStatusLabel(item) }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">Next Billing</span>
          <span class="meta-value">{{ formatDate(item.next_billing_date) }}</span>
        </div>
        <div class="history-cta">
          <span>View details, billing history, and manage options</span>
          <button class="view-history-btn" @click.stop="openDetails(item)">View</button>
        </div>
      </div>

      <div class="text-center mt-2" v-if="hasMore">
        <button class="load-more-btn" :disabled="isFetchingMore" @click="loadMore">
          {{ isFetchingMore ? 'Loading...' : 'Load More' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import subscriptionApi from "../../api/subscription";
import CanPayLoader from "../CustomLoader/CanPayLoader.vue";
import SubscriptionDatePicker from "./SubscriptionDatePicker.vue";
import moment from "moment";

export default {
  name: "SubscriptionList",
  components: { CanPayLoader, SubscriptionDatePicker },
  computed: {
    filteredSubscriptions() {
      return this.subscriptions || [];
    },
  },
  data() {
    return {
      isLoading: false,
      isFetchingMore: false,
      subscriptions: [],
      filterExpanded: false,
      page: 1,
      perPage: 10,
      hasMore: false,
      filters: {
        statusCode: "",
        searchText: "",
        fromDate: "",
        toDate: "",
        datePreset: {
          tenure: "all",
          from_date: "",
          to_date: "",
        },
      },
    };
  },
  methods: {
    loadData(reset = true) {
      if (reset) {
        this.page = 1;
        this.hasMore = false;
        this.isLoading = true;
      } else {
        this.isFetchingMore = true;
      }

      const request = {
        page: this.page,
        per_page: this.perPage,
      };

      if (this.filters.statusCode) {
        request.status_code = this.filters.statusCode;
      }
      if (this.filters.searchText) {
        request.search = this.filters.searchText;
      }
      if (this.filters.fromDate) {
        request.from_date = this.filters.fromDate;
      }
      if (this.filters.toDate) {
        request.to_date = this.filters.toDate;
      }

      subscriptionApi
        .listSubscriptions(request)
        .then((response) => {
          const responseData = response.data || {};
          const rows = Array.isArray(responseData) ? responseData : (responseData.data || []);
          const pagination = Array.isArray(responseData) ? null : responseData.pagination;

          if (reset) {
            this.subscriptions = rows;
          } else {
            this.subscriptions = [...this.subscriptions, ...rows];
          }

          this.hasMore = pagination ? !!pagination.has_more : rows.length === this.perPage;
          this.isLoading = false;
          this.isFetchingMore = false;
        })
        .catch(() => {
          this.isLoading = false;
          this.isFetchingMore = false;
        });
    },
    loadMore() {
      if (!this.hasMore || this.isFetchingMore) {
        return;
      }
      this.page += 1;
      this.loadData(false);
    },
    openDetails(item) {
      this.$router.push(`/subscriptions/${item.subscription_reference}`);
    },
    formatDate(value) {
      return value ? moment(value).format("MMM DD, YYYY hh:mm A") : "-";
    },
    formatMoney(value) {
      const amount = value === null || value === undefined ? 0 : value;
      return parseFloat(amount).toFixed(2);
    },
    formatPeriod(value) {
      if (!value) {
        return "-";
      }
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    resetFilters() {
      this.filters.statusCode = "";
      this.filters.searchText = "";
      this.filters.fromDate = "";
      this.filters.toDate = "";
      this.filters.datePreset = {
        tenure: "all",
        from_date: "",
        to_date: "",
      };
      this.loadData(true);
    },
    onDatePresetChange(payload) {
      this.filters.datePreset = payload;
      this.filters.fromDate = payload.from_date || "";
      this.filters.toDate = payload.to_date || "";
    },
    applyFilters() {
      this.loadData(true);
    },
    isActiveStatus(item) {
      if (Number(item.status_code) === 204) {
        return true;
      }
      return String(item.status || "").toLowerCase() === "active";
    },
    getSubscriptionStatusLabel(item) {
      if (this.isActiveStatus(item)) {
        return "Active";
      }

      const reason = String(item.failed_reason || "").toLowerCase();
      const statusText = String(item.status || "").toLowerCase();
      const statusCode = Number(item.status_code);

      if (reason.includes("consumer declined") || reason.includes("declined by consumer") || reason.includes("cancelled by consumer") || reason.includes("canceled by consumer")) {
        return "Consumer Declined";
      }

      if (statusCode === 1203 || statusCode === 205 || statusText.includes("declined") || statusText.includes("failed") || reason.length > 0) {
        return "Failed";
      }

      return item.status || "Failed";
    },
  },
  mounted() {
    this.loadData();
    this.$root.$emit("changeWhiteBackground", [false, false, "MembershipSubscriptionsHeader"]);
  },
};
</script>

<style scoped>
.subscription-page {
  min-height: 100vh;
  background-color: #149240;
  padding-bottom: 20px;
}

.subscription-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #e4e8e6;
  box-shadow: 0 2px 6px rgba(13, 84, 42, 0.08);
}

.filters-card {
  background: #ffffff;
  border: 1px solid #e4e8e6;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(13, 84, 42, 0.08);
}

.filter-toggle {
  width: 100%;
  border: 0;
  background: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
}

.filter-toggle-title {
  font-size: 14px;
  font-weight: 700;
  color: #155d31;
}

.filter-toggle-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #eaf5ee;
  color: #155d31;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  line-height: 1;
}

.filters-body {
  padding: 0 12px 12px;
  border-top: 1px solid #e4ece7;
}

.filter-search {
  margin-top: 10px;
  margin-bottom: 8px;
}

.filters-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.date-filter {
  grid-column: 1 / -1;
}

.filter-input {
  width: 100%;
  height: 40px;
  border: 1px solid #d5ddd8;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  color: #1f1f1f;
  background: #fff;
  outline: none;
}

.filter-input:focus {
  border-color: #149240;
  box-shadow: 0 0 0 2px rgba(20, 146, 64, 0.14);
}

.apply-filter-btn,
.clear-filter-btn {
  border: 1px solid #b6c1bb;
  background: #f2f4f3;
  color: #30423a;
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 700;
}

.filter-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}

.apply-filter-btn {
  border-color: #149240;
  background: #e9f7ef;
  color: #0f5f2c;
}

.load-more-btn {
  border: 1px solid #149240;
  background: #f0fbf4;
  color: #0f5f2c;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
}

.card-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.subscription-title {
  font-weight: 700;
  color: #0f5f2c;
  font-size: 18px;
  line-height: 1.2;
  margin-bottom: 0;
}

.subscription-description {
  margin-top: 5px;
  font-size: 12px;
  color: #4f5f58;
  line-height: 1.3;
}

.subscription-reference {
  margin-top: 5px;
  font-size: 11px;
  font-weight: 700;
  color: #5f6e67;
  word-break: break-word;
}

.amount {
  font-size: 20px;
  font-weight: 700;
  color: #1f1f1f;
  margin-bottom: 0;
  white-space: nowrap;
}

.period-chip {
  display: inline-block;
  margin-top: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #21643c;
  background: #edf8f0;
  border: 1px solid #cce6d4;
  border-radius: 999px;
  padding: 2px 8px;
}

.meta-separator {
  border-top: 1px dashed #d5ddd8;
  margin: 8px 0 7px;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.meta-label {
  font-size: 12px;
  color: #6f7471;
}

.meta-value {
  font-size: 12px;
  font-weight: 600;
  color: #232323;
  text-align: right;
}

.status-pill {
  font-size: 11px;
  color: #0d5a2b;
  background: #ecf7f0;
  border: 1px solid #cde7d6;
  border-radius: 999px;
  padding: 2px 7px;
}

.status-pill-inactive {
  font-size: 11px;
  color: #7a2424;
  background: #fff0f0;
  border: 1px solid #efcaca;
  border-radius: 999px;
  padding: 2px 7px;
}

.history-cta {
  margin-top: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #6a6f6c;
}

.view-history-btn {
  border: 1px solid #149240;
  background: #f0fbf4;
  color: #0f5f2c;
  border-radius: 8px;
  padding: 4px 11px;
  font-size: 12px;
  font-weight: 700;
}
</style>
